import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { MagnifyingGlass, Sun, Moon, Plus, Bell, CaretDown } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export function Topbar({ onMobileNav }) {
  const { theme, toggleTheme, brand, setBrand, brands, setPaletteOpen } = useApp();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: notifications = [] } = useQuery({
    queryKey: ["my-notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 20000,
  });
  const unread = notifications.filter((n) => !n.read).length;
  const allBrands = [{ id: "all", name: "All brands", color: "#7C3AED" }, ...brands];
  const active = allBrands.find((b) => b.name === brand) || allBrands[0];

  return (
    <header className="glass sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border px-4 sm:px-6">
      <button onClick={onMobileNav} className="lg:hidden rounded-lg p-2 hover:bg-secondary" data-testid="mobile-nav-toggle">
        <span className="block h-0.5 w-5 bg-foreground" />
        <span className="mt-1 block h-0.5 w-5 bg-foreground" />
        <span className="mt-1 block h-0.5 w-5 bg-foreground" />
      </button>

      {/* Brand switcher */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="brand-switcher" className="flex items-center gap-2 rounded-full border border-border bg-surface-raised px-3 py-1.5 text-sm font-semibold transition-colors hover:bg-secondary">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: active.color }} />
            <span className="max-w-[120px] truncate">{active.name}</span>
            <CaretDown size={14} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel>Switch brand</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {allBrands.map((b) => (
            <DropdownMenuItem key={b.id} data-testid={`brand-option-${b.name.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setBrand(b.name)} className="gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: b.color }} />
              {b.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Search / command */}
      <button
        data-testid="global-search"
        onClick={() => setPaletteOpen(true)}
        className="ml-1 flex flex-1 items-center gap-2 rounded-full border border-border bg-surface-raised px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary max-w-md"
      >
        <MagnifyingGlass size={16} />
        <span className="hidden sm:inline">Search or jump to…</span>
        <kbd className="ml-auto hidden rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold sm:inline">⌘K</kbd>
      </button>

      <div className="ml-auto flex items-center gap-1.5">
        <button data-testid="quick-create" onClick={() => navigate("/campaigns")} className="hidden items-center gap-1.5 rounded-full bg-gradient-to-r from-brand-violet to-brand-pink px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 sm:flex">
          <Plus size={16} weight="bold" /> Create
        </button>
        <button onClick={() => navigate("/notifications")} className="relative rounded-full p-2 hover:bg-secondary" data-testid="notifications-btn">
          <Bell size={20} weight="duotone" />
          {unread > 0 && (
            <span data-testid="notif-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-pink px-1 text-[10px] font-bold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
        <button onClick={toggleTheme} className="rounded-full p-2 hover:bg-secondary" data-testid="theme-toggle">
          {theme === "dark" ? <Sun size={20} weight="duotone" /> : <Moon size={20} weight="duotone" />}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu" className="ml-1 flex items-center gap-2 rounded-full p-0.5 hover:bg-secondary">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <p className="font-semibold">{user?.name}</p>
              <p className="text-xs font-normal capitalize text-muted-foreground">{user?.role?.replace(/_/g, " ")}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")}>Settings</DropdownMenuItem>
            <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-brand-pink">Log out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
